import type { TranscriptSnippet } from "@expert/shared-types";

// A snippet has no id, so its identity is the (timestamp, speaker, content) tuple. GAIA re-delivers
// already-seen snippets verbatim whenever the client re-subscribes on a websocket reconnect, so
// keying on this tuple collapses those exact re-deliveries while still keeping genuinely distinct
// utterances that share a timestamp (different content) or repeat the same words at a different time.
const identity = (snippet: TranscriptSnippet): string =>
    [snippet.timestamp, snippet.speaker, snippet.content].join("\t");

export const MAX_RETAINED_SNIPPETS = 200;

const sameSnippets = (a: TranscriptSnippet[], b: TranscriptSnippet[]): boolean =>
    a.length === b.length && a.every((snippet, i) => snippet === b[i]);

/*
 * Merge two runs of snippets into a single timestamp-ascending list, dropping duplicates. Snippets
 * can arrive out of order and can be re-delivered on reconnect, so we both sort and dedupe.
 */
export const mergeSnippets = (
    sortedSnippets: TranscriptSnippet[] | undefined,
    newSnippets: TranscriptSnippet[] | undefined,
) => {
    // if no new snippets, return existing snippets unchanged
    if (!newSnippets || newSnippets.length === 0) return sortedSnippets;

    newSnippets.sort((a, b) => a.timestamp - b.timestamp);

    const existing = sortedSnippets ?? [];

    const mergedArray: TranscriptSnippet[] = [];
    const seen = new Set<string>();
    let i = 0;
    let j = 0;

    const push = (snippet: TranscriptSnippet) => {
        const key = identity(snippet);
        if (seen.has(key)) return;
        seen.add(key);
        mergedArray.push(snippet);
    };

    // Traverse both arrays and merge them in sorted order, skipping any duplicate identity
    while (i < existing.length && j < newSnippets.length) {
        if (existing[i].timestamp <= newSnippets[j].timestamp) {
            push(existing[i]);
            i++;
        } else {
            push(newSnippets[j]);
            j++;
        }
    }

    while (i < existing.length) {
        push(existing[i]);
        i++;
    }

    while (j < newSnippets.length) {
        push(newSnippets[j]);
        j++;
    }

    const capped = mergedArray.length > MAX_RETAINED_SNIPPETS ? mergedArray.slice(-MAX_RETAINED_SNIPPETS) : mergedArray;

    // Nothing the caller can see changed — either a pure re-delivery of duplicates, or a late
    // out-of-order snippet older than the retained window that the cap drops. Return the original
    // reference so React/Virtuoso skip the re-render.
    if (sortedSnippets && sameSnippets(capped, sortedSnippets)) return sortedSnippets;

    return capped;
};
